import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { ClusterStatus, MatchTier, SubjectKind } from '@hp/domain';
import { invalidateAll } from '../../api/queryClient';
import { trpc, type RouterOutputs } from '../../api/trpc';

export type ClusterListItem = RouterOutputs['review']['listClusters']['items'][number];
export type ClusterDetail = RouterOutputs['review']['getCluster'];
export type ClusterSummary = ClusterDetail['cluster'];
export type ClusterMember = ClusterDetail['members'][number];
export type ClusterEndpoint = ClusterDetail['endpoints'][number];
export type ClusterPair = ClusterDetail['pairs'][number];
export type PastDecision = ClusterDetail['decisions'][number];
export type QueueSummaryRow = RouterOutputs['review']['queueSummary'][number];

export interface ClusterFilters {
  entityType: SubjectKind | '';
  tier: MatchTier | '';
  status: ClusterStatus;
}

export function useClusters(filters: ClusterFilters, limit = 50) {
  return useInfiniteQuery(
    trpc.review.listClusters.infiniteQueryOptions(
      {
        entityType: filters.entityType || undefined,
        tier: filters.tier || undefined,
        status: filters.status,
        limit,
      },
      { getNextPageParam: (last) => last.nextCursor },
    ),
  );
}

/** Cluster/record counts per entity type × tier × status — the stats strip and "n of N loaded". */
export function useQueueSummary() {
  return useQuery(trpc.review.queueSummary.queryOptions(undefined, { staleTime: 15_000 }));
}

/** Clusters matching the list filters, from the summary matrix (undefined while it loads). */
export function countMatching(rows: QueueSummaryRow[] | undefined, filters: ClusterFilters): number | undefined {
  if (!rows) return undefined;
  return rows
    .filter(
      (r) =>
        (!filters.entityType || r.entityType === filters.entityType) &&
        (!filters.tier || r.tier === filters.tier) &&
        r.status === filters.status,
    )
    .reduce((n, r) => n + r.clusters, 0);
}

export function useCluster(id: number) {
  return useQuery(trpc.review.getCluster.queryOptions({ id }, { enabled: Number.isFinite(id) && id > 0 }));
}

/** Ids of pending clusters (id desc) for j/k navigation in the detail screen. */
export function usePendingClusterIds(entityType: SubjectKind | undefined) {
  return useQuery(
    trpc.review.listClusters.queryOptions(
      { status: 'pending', limit: 200, entityType },
      { enabled: entityType !== undefined, staleTime: 15_000, select: (page) => page.items.map((i) => i.id) },
    ),
  );
}

/** Drop every cached review query (after a decision, bulk approve, or a merge). */
export function invalidateReview(): Promise<void> {
  return invalidateAll(trpc.review.pathFilter());
}
